import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, CheckCircle2, XCircle, ArrowLeft, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function QuizElevePage() {
  const [quizzes, setQuizzes] = useState([]);
  const [current, setCurrent] = useState(null); // quiz en cours {id, questions...}
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/pedagogy-ai/quiz/student").then(r => setQuizzes(r.data)).catch(() => toast.error("Erreur de chargement"));
  useEffect(() => { load(); }, []);

  const start = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pedagogy-ai/quiz/${q.id}/take`);
      setCurrent(data);
      setAnswers(new Array(data.questions.length).fill(-1));
      setResult(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (answers.includes(-1) && !window.confirm("Certaines questions sont sans réponse. Soumettre quand même ?")) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/pedagogy-ai/quiz/${current.id}/submit`, { answers });
      setResult(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  if (current) {
    return (
      <div className="space-y-5 max-w-3xl" data-testid="quiz-take-view">
        <Button variant="outline" onClick={() => { setCurrent(null); setResult(null); }} className="gap-1 text-xs" data-testid="quiz-back-btn">
          <ArrowLeft className="h-3 w-3" />Retour aux sujets
        </Button>
        <div>
          <div className="chip mb-2"><Sparkles className="h-3 w-3" /><span>{current.subject} · {current.difficulty}</span></div>
          <h1 className="font-heading font-extrabold text-2xl text-[#1E3A8A]">{current.topic}</h1>
        </div>

        {result && (
          <div className="bg-white rounded-2xl border-2 border-[#166534] p-5 flex items-center gap-4" data-testid="quiz-result">
            <Trophy className="h-10 w-10 text-[#EA580C]" />
            <div>
              <div className="font-heading font-extrabold text-3xl text-[#166534]">{result.score} / {result.total}</div>
              <div className="text-sm text-slate-600">{result.score >= result.total * 0.7 ? "Excellent travail !" : result.score >= result.total * 0.5 ? "Bien, continue tes efforts !" : "Revois la leçon et réessaie !"}</div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {(result ? result.corrections : current.questions).map((q, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`question-${i}`}>
              <div className="font-semibold text-slate-800 mb-3">{i + 1}. {q.question}</div>
              <div className="space-y-2">
                {q.choices.map((c, ci) => {
                  let cls = "border-slate-200 hover:border-[#1E3A8A]/50";
                  if (result) {
                    if (ci === q.correct_index) cls = "border-green-500 bg-green-50";
                    else if (ci === q.given_index && !q.correct) cls = "border-red-400 bg-red-50";
                    else cls = "border-slate-100 opacity-60";
                  } else if (answers[i] === ci) cls = "border-[#1E3A8A] bg-blue-50";
                  return (
                    <button key={ci} type="button" disabled={!!result}
                      onClick={() => { const a = [...answers]; a[i] = ci; setAnswers(a); }}
                      className={`w-full text-left text-sm border rounded-lg px-3 py-2 transition-colors ${cls}`}
                      data-testid={`q${i}-choice-${ci}`}>
                      {c}
                      {result && ci === q.correct_index && <CheckCircle2 className="h-4 w-4 inline ml-2 text-green-600" />}
                      {result && ci === q.given_index && !q.correct && <XCircle className="h-4 w-4 inline ml-2 text-red-500" />}
                    </button>
                  );
                })}
              </div>
              {result && q.explanation && (
                <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-3">
                  💡 {q.explanation}
                </div>
              )}
            </div>
          ))}
        </div>

        {!result && (
          <Button onClick={submit} disabled={loading} className="bg-[#166534] hover:bg-[#14532d] text-white w-full h-12" data-testid="quiz-submit-btn">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Soumettre mes réponses"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quiz-list-view">
      <div>
        <div className="chip mb-2"><Sparkles className="h-3 w-3" /><span>IA PÉDAGOGIQUE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Sujets d&apos;entraînement IA</h1>
        <p className="text-sm text-slate-500 mt-1">Quiz générés par tes enseignants avec l&apos;IA. Entraîne-toi et obtiens ta correction instantanée !</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {quizzes.map(q => (
          <div key={q.id} className={`bg-white rounded-2xl border p-5 flex flex-col gap-2 ${q.recommended ? "border-[#166534] shadow-md" : "border-slate-200"}`} data-testid={`quiz-card-${q.id}`}>
            {q.recommended && <span className="self-start text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800">Recommandé pour ta classe</span>}
            <div className="font-heading font-bold text-[#0F172A]">{q.topic}</div>
            <div className="text-xs text-slate-500">{q.subject} · {q.class_level} · {q.difficulty} · {q.num_questions} questions</div>
            {q.last_score && <div className="text-xs font-semibold text-[#EA580C]">Dernier score : {q.last_score}</div>}
            <Button onClick={() => start(q)} disabled={loading} size="sm" className="mt-auto bg-[#1E3A8A] text-white gap-1" data-testid={`start-quiz-${q.id}`}>
              <Sparkles className="h-3 w-3" />{q.last_score ? "Réessayer" : "Commencer"}
            </Button>
          </div>
        ))}
        {quizzes.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">
            Aucun sujet disponible pour le moment. Tes enseignants en publieront bientôt !
          </div>
        )}
      </div>
    </div>
  );
}
